'use client';

import { createContext, ReactNode, useContext } from "react";
import { useSingleTypeEditor, UseSingleTypeEditorType } from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import {
    SiteGuidedToursEditable,
    SiteGuidedToursSchema
} from "@/app/features/sites/guided-tours/types/SiteGuidedTours.schema";
import { SiteGuidedTour, SiteGuidedTourInput } from "@/app/lib/generated/graphql-types";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { getGuidedToursSite } from "@/app/features/sites/guided-tours/data-access/queries";
import { updateSiteGuidedTours } from "@/app/features/sites/guided-tours/data-access/mutations";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";

const GuidedToursEditorContext = createContext<UseSingleTypeEditorType<SiteGuidedToursEditable> | undefined>(undefined);

export function useSiteGuidedToursEditor() {
    const context = useContext(GuidedToursEditorContext);
    if (!context) {
        throw new Error("useSiteGuidedToursEditor must be used within a GuidedToursEditorProvider");
    }
    return context;
}

interface GuidedToursEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function GuidedToursEditorProvider({ children, isDraftModeEnabled }: GuidedToursEditorProviderProps) {
    const editor = useSingleTypeEditor<SiteGuidedTour, SiteGuidedToursEditable, SiteGuidedTourInput>({
        schema: SiteGuidedToursSchema,
        loadEntity: getGuidedToursSite,
        update: updateSiteGuidedTours,
    })

    if (editor.isLoading) return <LoadingSpinner />

    return (
        <GuidedToursEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </GuidedToursEditorContext.Provider>
    )
}
